import { Component, Signal, ChangeDetectionStrategy } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { PrimaryButtonComponent } from 'src/app/shared/components/primary-button/primary-button.component';
import { UrlParamsService } from 'src/app/shared/services/url-params.service';
import { SubscriptionService } from '../../subscription.service';
import { PublicStakeholderDTO } from '../../types/PublicStakeholderDTO';
import { MinProjectDTO } from '../../types/MinProjectDTO';
import { Subscription } from 'rxjs';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { ToastrNotificationService } from 'src/app/shared/services/toastr-notification.service';
import { ToastrType } from 'src/app/shared/types/ToastrType.enum';
import { LoadingSpinnerOverlayComponent } from 'src/app/shared/components/loading-spinner-overlay/loading-spinner-overlay.component';
import { FormLayoutComponent } from 'src/app/shared/components/form-layout/form-layout.component';
import { GlobalEditStakeholderComponent } from 'src/app/shared/components/global-edit-stakeholder/global-edit-stakeholder.component';
import { LookupsDTO } from 'src/app/shared/types/LookupsDTO';

@Component({
  selector: 'app-edit-subscription',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatCheckboxModule,
    LoadingSpinnerOverlayComponent,
    GlobalEditStakeholderComponent,
  ],
  templateUrl: './edit-subscription.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-subscription.component.scss',
})
export class EditSubscriptionComponent {
  isLoadingEditStakeholder: Signal<boolean>;
  stakeholderInfo: PublicStakeholderDTO = {} as PublicStakeholderDTO;
  isEdit: boolean = false;
  projectInfo: MinProjectDTO | null = null;
  private subscription: Subscription | null = null;
  states: any[] = [];
  stakeholderTypes: any[] = [];
  organizationTypes: any[] = [];
  commentTopics: any[] = [];
  commentChoices: any[] = [];
  travelList: LookupsDTO[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private urlParamsService: UrlParamsService,
    private route: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    private lookupService: LookupService,
    private toastrNotification: ToastrNotificationService
  ) {
    this.isLoadingEditStakeholder = this.subscriptionService.isLoadingEditStakeholder$;
  }

  ngOnInit(): void {
    const state = history.state;
    if (state && !state.isEdit) {
      this.stakeholderInfo = {
        ...this.stakeholderInfo,
        LastName: state.LastName,
        Mail1ZipCode: state.Mail1ZipCode,
        StakeEmail: state.StakeEmail,
        SubscribeSourceId: 4,
      };
      this.isEdit = state.isEdit;
    }

    if (this.urlParamsService.stakeGuid) {
      this.loadStakeholderDetails();
    }

    this.subscription = this.subscriptionService.projectInfo$.subscribe({
      next: (projectInfo) => {
        this.projectInfo = projectInfo;
      },
      error: (error) => {
        console.error('Error fetching project info:', error);
      },
    });
  }

  loadStakeholderDetails(): void {
    this.route.queryParams.subscribe((params) => {
      const stakeGuid = params['stakeGuid'];
      const projectGuid = params['projectGuid'];
      if (stakeGuid && projectGuid) {
        this.subscriptionService
          .getStakeholderByProjectAndStakeGuid(stakeGuid, projectGuid)
          .subscribe({
            next: (response) => {
              this.isEdit = true;
              this.stakeholderInfo = response;
              this.stakeholderInfo.SubscribeSourceId = 4;
              if (response.TravelList) {
                this.travelList = response.TravelList;
              }
            },
            error: (error) => {
              console.error('Error fetching stakeholder details:', error);
            },
          });
      }
    });
  }

  goBack(): void {
    this.urlParamsService.navigateWithParams('/subscription/search');
  }

  handleStakeholderEditCreateResult(event: { status: string; data: any }) {
    switch (event.status) {
      case 'success':
        /*
         * We aren't allowing for editing of the stakeholder if match was found...
         * There will be a unique link sent via email to edit your stakeholder information,
         * But we are not at that part yet so keeping this here for now, but I assume it would redirect to a success page
         */
        const isSubscribed = event.data.IsReceiveEmailUpdate ?? false;
        this.triggerEmail(isSubscribed);
        this.urlParamsService.navigateWithParams('/subscription/confirm');
        break;
      case 'error':
        console.error('Error during service call:', event.data);

        break;
    }
  }

  triggerEmail(isSubscribed: boolean = true) {
    this.route.queryParams.subscribe((params) => {
      const stakeGuid = params['stakeGuid'];
      const projectGuid = params['projectGuid'];
      if (stakeGuid && projectGuid) {
        this.subscription = this.subscriptionService
          .triggerConfirmationSubscriptionEmail(projectGuid, stakeGuid, isSubscribed)
          .subscribe({
            error: (error) => {
              console.error('Error sending confirmation email:', error);
            },
          });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
